import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../utils/api';

// PUBLIC client share page for one workflow instance — /w/:token, no login (the 32-hex random
// token IS the credential). English only (client-facing, same convention as the POA share page).
// Owner-confirmed rules: client uploads / confirms flip a waiting step to In Progress; only SLF
// marks steps complete; blocked steps are locked (server enforces with 409, UI shows the lock).

const G = '#0E7C66';
const fmt = (n: any) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtTs = (ts: number) => { try { return new Date(ts * 1000).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' }) + ' (BKK)'; } catch { return ''; } };

const PARTY_META: Record<string, { label: string; bg: string; c: string }> = {
  client: { label: 'CLIENT', bg: '#EFF6FF', c: '#1D4ED8' },
  agent: { label: 'AGENT', bg: '#FFF7ED', c: '#C2410C' },
  slf: { label: 'SLF', bg: '#ECFDF5', c: G },
};
const STATUS_META: Record<string, { l: string; bg: string; c: string }> = {
  waiting: { l: 'WAITING', bg: '#F3F4F6', c: '#6B7280' },
  in_progress: { l: 'IN PROGRESS', bg: '#FEF9C3', c: '#A16207' },
  complete: { l: 'COMPLETE', bg: '#DCFCE7', c: '#15803D' },
};

// `preview` renders the page from a staff-supplied payload instead of a share token: identical
// markup (that IS the point — it must be the real thing), every action inert, plus a banner.
export default function WfShare({ preview }: { preview?: { data: any; fileUrl: (id: string, inline?: boolean) => string; onClose?: () => void } } = {}) {
  const { token = '' } = useParams();
  const [data, setData] = useState<any>(preview ? preview.data : null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');
  const [lightbox, setLightbox] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const target = useRef<{ stepId: string; slot: string }>({ stepId: '', slot: '' });

  const load = () => (preview ? Promise.resolve() : api.wfShareGet(token).then(setData).catch((e: any) => setErr(e.message)));
  useEffect(() => { if (!preview) load(); }, [token, preview]);   // eslint-disable-line
  // Preview works on a deep COPY so the tester can click freely; nothing here touches the server.
  const reset = () => setData(JSON.parse(JSON.stringify(preview!.data)));
  useEffect(() => { if (preview) reset(); }, [preview]);   // eslint-disable-line

  // Sandbox mutations — the same rules the live page enforces, applied to local state only.
  const simPatch = (stepId: string, patch: any) => setData((d: any) => ({ ...d, steps: d.steps.map((x: any) => (x.id === stepId ? { ...x, ...patch } : x)) }));
  const simStatus = (stepId: string, status: string) => simPatch(stepId, { status, statusAt: Math.floor(Date.now() / 1000) });
  const simStart = (st: any) => { if (st.status === 'waiting') return { status: 'in_progress', statusAt: Math.floor(Date.now() / 1000) }; return {}; };
  const simUpload = (st: any, slot: string, f: File) => {
    const url = URL.createObjectURL(f);
    const file = { id: `sim-${Date.now()}`, slot, byParty: (st.parties || []).includes('client') ? 'client' : 'slf', byName: (st.parties || []).includes('client') ? 'you' : 'SLF', filename: f.name, mime: f.type, size: f.size, at: Math.floor(Date.now() / 1000), simUrl: url };
    simPatch(st.id, { files: [...(st.files || []), file], ...simStart(st) });
  };
  const simConfirm = (st: any) => simPatch(st.id, {
    confirmed: true, ...simStart(st),
    comments: [...(st.comments || []), { party: 'client', name: 'you', body: `✔ ${st.confirmLabel}`, at: Math.floor(Date.now() / 1000) }],
  });
  const simComment = (st: any, body: string) => simPatch(st.id, { comments: [...(st.comments || []), { party: 'client', name: 'you', body, at: Math.floor(Date.now() / 1000) }] });
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(''); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [lightbox]);

  const pick = (stepId: string, slot: string) => { target.current = { stepId, slot }; fileInput.current?.click(); };
  const onFile = async (e: any) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    const { stepId, slot } = target.current;
    if (preview) { const st = (data.steps || []).find((x: any) => x.id === stepId); if (st) simUpload(st, slot, f); return; }
    setBusy(`up-${stepId}-${slot}`);
    try { await api.wfShareUpload(token, stepId, slot, f); await load(); }
    catch (er: any) { alert(er.message); } finally { setBusy(''); }
  };
  const confirm = async (stepId: string) => {
    if (preview) { const st = (data.steps || []).find((x: any) => x.id === stepId); if (st) simConfirm(st); return; }
    setBusy(`cf-${stepId}`);
    try { await api.wfShareConfirm(token, stepId); await load(); } catch (er: any) { alert(er.message); } finally { setBusy(''); }
  };
  const comment = async (stepId: string) => {
    const body = (note[stepId] || '').trim();
    if (!body || busy === `cm-${stepId}`) return;
    if (preview) { const st = (data.steps || []).find((x: any) => x.id === stepId); if (st) { simComment(st, body); setNote(p => ({ ...p, [stepId]: '' })); } return; }
    setBusy(`cm-${stepId}`);
    try { await api.wfShareComment(token, stepId, body); setNote(p => ({ ...p, [stepId]: '' })); await load(); }
    catch (er: any) { alert(er.message); } finally { setBusy(''); }
  };
  const delFile = async (fileId: string) => {
    if (preview) {
      const st = (data.steps || []).find((x: any) => (x.files || []).some((f: any) => f.id === fileId));
      if (st) simPatch(st.id, { files: (st.files || []).filter((f: any) => f.id !== fileId) });
      return;
    }
    if (!window.confirm('Remove this file?')) return;
    try { await api.wfShareFileDelete(token, fileId); await load(); } catch (er: any) { alert(er.message); }
  };

  if (err) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif', color: '#6B7280', fontSize: 14 }}>This link is not valid or has been replaced. Please contact SLF for a new link.</div>;
  if (!data) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="ti ti-loader-2" style={{ fontSize: 22, color: G, animation: 'spin .8s linear infinite' }} /></div>;

  // Locks are recomputed on every render in preview so flipping a status live-unlocks what depends
  // on it — that is what makes the sandbox a real test of the dependency design.
  const view = (() => {
    if (!preview) return data;
    const byId = new Map<string, any>((data.steps || []).map((x: any) => [x.id, x]));
    const steps = (data.steps || []).map((x: any) => {
      const blocking = (x.deps || []).map((id: string) => byId.get(id)).filter((d: any) => d && d.status !== 'complete').map((d: any) => d.n).sort((a: number, b: number) => a - b);
      return { ...x, blockedBy: blocking[0] || 0, blockedSteps: blocking };
    });
    return { ...data, steps, progress: { done: steps.filter((x: any) => x.status === 'complete').length, total: steps.length } };
  })();
  const fileUrl = (id: string, inline = false) => {
    if (!preview) return api.wfShareFileUrl(token, id, inline);
    const f = (view.steps || []).flatMap((x: any) => x.files || []).find((x: any) => x.id === id);
    return f?.simUrl || preview.fileUrl(id, inline);
  };
  const p = view.price || {};
  const pct = view.progress?.total ? Math.round(view.progress.done * 100 / view.progress.total) : 0;

  return (
    <div style={{ minHeight: '100vh', background: '#F6F8F7', fontFamily: 'Inter, system-ui, sans-serif', color: '#111827' }}>
      <input ref={fileInput} type="file" style={{ display: 'none' }} onChange={onFile} />
      {lightbox && (
        <div onClick={() => setLightbox('')} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: 20 }}>
          <img src={lightbox} alt="" style={{ maxWidth: '96vw', maxHeight: '94vh', borderRadius: 8 }} />
        </div>
      )}
      {preview && (
        <div style={{ background: '#1F2937', color: '#fff', padding: '9px 20px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, fontWeight: 600 }}>
          <i className="ti ti-eye" />
          <span style={{ flex: 1 }}>Preview sandbox — click anything to test the flow: set statuses, upload files, confirm, comment. Everything stays in this window; nothing is saved.</span>
          <button onClick={reset} style={{ border: '1px solid #4B5563', background: 'transparent', color: '#fff', borderRadius: 7, padding: '4px 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}><i className="ti ti-rotate" /> Reset</button>
          {preview.onClose && <button onClick={preview.onClose} style={{ border: '1px solid #4B5563', background: 'transparent', color: '#fff', borderRadius: 7, padding: '4px 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Close</button>}
        </div>
      )}
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: `3px solid ${G}` }}>
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '26px 20px 20px' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.14em', color: G }}>{String(view.companyName || 'SLF').toUpperCase()}</div>
          <h1 style={{ margin: '6px 0 2px', fontSize: 24, fontWeight: 800 }}>{view.title}</h1>
          <div style={{ fontSize: 13.5, color: '#6B7280' }}>Client: <b style={{ color: '#111827' }}>{view.clientName}</b></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
            <div style={{ flex: 1, height: 8, borderRadius: 999, background: '#E5E7EB', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: G, transition: 'width .4s' }} />
            </div>
            <span style={{ fontSize: 12.5, fontWeight: 800, color: G, whiteSpace: 'nowrap' }}>{view.progress.done} / {view.progress.total} steps</span>
          </div>
          {view.status === 'cancelled' && <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 700, color: '#DC2626' }}>This workflow has been cancelled.</div>}
          {view.status === 'complete' && <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 700, color: '#15803D' }}><i className="ti ti-circle-check" /> All steps complete — thank you.</div>}
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 20px 60px' }}>
        {/* Fees */}
        {p.unitPrice != null && (
          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px', marginBottom: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: '#6B7280', marginBottom: 10 }}>
              FEES{view.sampleFee ? <span style={{ marginLeft: 8, fontWeight: 700, color: '#B45309', letterSpacing: 0, textTransform: 'none' }}>sample figure — a real workflow shows the service's own price</span> : null}
            </div>
            <Row l={`${p.name || 'Service fee'} × ${p.quantity}`} v={`${fmt(p.preVat)} THB`} />
            {p.vat > 0 && <Row l={`VAT ${p.vatRate}%`} v={`${fmt(p.vat)} THB`} />}
            <div style={{ borderTop: '1px solid #E5E7EB', marginTop: 6, paddingTop: 6 }}>
              <Row l="Total" v={`${fmt(p.total)} THB`} strong={!(p.wht > 0)} />
              {p.wht > 0 && <Row l={`Withholding tax ${p.whtRate}%`} v={`− ${fmt(p.wht)} THB`} />}
              {p.wht > 0 && <Row l="Net to pay" v={`${fmt(p.net)} THB`} strong />}
            </div>
          </div>
        )}

        {/* Steps */}
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: '#6B7280', marginBottom: 10 }}>PROCESS</div>
        {(view.steps || []).map((s: any) => {
          const sm = STATUS_META[s.status] || STATUS_META.waiting;
          const clientStep = (s.parties || []).includes('client');
          const locked = !!s.blockedBy;
          const images = (s.files || []).filter((f: any) => /^image\//.test(f.mime) && f.slot === 'example');
          const uploadsBySlot = (slot: string) => (s.files || []).filter((f: any) => f.slot === slot && !(f.slot === 'example' && /^image\//.test(f.mime)));
          return (
            <div key={s.id} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px', marginBottom: 12, opacity: locked ? 0.72 : 1 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ width: 30, height: 30, borderRadius: 999, background: s.status === 'complete' ? '#15803D' : locked ? '#9CA3AF' : G, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13.5, fontWeight: 800, flexShrink: 0 }}>
                  {s.status === 'complete' ? <i className="ti ti-check" /> : locked ? <i className="ti ti-lock" /> : s.n}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 800 }}>{s.title}</span>
                    {(s.parties || []).map((pt: string) => { const m = PARTY_META[pt] || PARTY_META.slf; return <span key={pt} style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em', padding: '2px 8px', borderRadius: 5, background: m.bg, color: m.c }}>{m.label}</span>; })}
                    {preview ? (
                      <span style={{ marginLeft: 'auto', display: 'inline-flex', border: '1px solid #D1D5DB', borderRadius: 8, overflow: 'hidden' }}>
                        {(['waiting', 'in_progress', 'complete'] as const).map(st => (
                          <button key={st} onClick={() => simStatus(s.id, st)}
                            style={{ padding: '4px 10px', border: 'none', fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', cursor: 'pointer', whiteSpace: 'nowrap',
                              background: s.status === st ? (st === 'complete' ? '#15803D' : st === 'in_progress' ? '#A16207' : '#6B7280') : '#fff',
                              color: s.status === st ? '#fff' : '#6B7280' }}>{STATUS_META[st].l}</button>
                        ))}
                      </span>
                    ) : (
                      <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 800, letterSpacing: '.06em', padding: '3px 10px', borderRadius: 999, background: sm.bg, color: sm.c, whiteSpace: 'nowrap' }}>{sm.l}</span>
                    )}
                  </div>
                  {s.detail && <div style={{ fontSize: 13, color: '#4B5563', marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{s.detail}</div>}
                  {s.statusAt ? <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5 }}>{fmtTs(s.statusAt)}</div> : null}
                  {locked ? <div style={{ fontSize: 12, fontWeight: 700, color: '#B45309', marginTop: 8 }}><i className="ti ti-lock" /> Locked until {(s.blockedSteps || [s.blockedBy]).length > 1 ? `Steps ${(s.blockedSteps || []).join(', ')} are` : `Step ${s.blockedBy} is`} complete</div> : null}

                  {/* Example images */}
                  {images.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      {images.map((f: any) => (
                        <img key={f.id} src={fileUrl(f.id, true)} alt={f.filename} onClick={() => setLightbox(fileUrl(f.id, true))}
                          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          style={{ height: 84, borderRadius: 8, border: '1px solid #E5E7EB', cursor: 'zoom-in' }} />
                      ))}
                    </div>
                  )}

                  {/* Upload slots */}
                  {(s.uploads || []).map((u: any) => (
                    <div key={u.key} style={{ marginTop: 10, border: '1px dashed #CBD5D1', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#374151' }}>{u.label}</span>
                        {/* In preview EVERY slot shows its button (inert) so staff can judge the layout;
                            the chip says who will actually get it on the live page. */}
                        {preview ? (
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em', padding: '2px 8px', borderRadius: 5, background: clientStep ? '#EFF6FF' : '#ECFDF5', color: clientStep ? '#1D4ED8' : G }}>
                              {clientStep ? 'CLIENT UPLOADS' : 'SLF UPLOADS'}
                            </span>
                            <button onClick={() => pick(s.id, u.key)} disabled={locked}
                              title={locked ? 'Locked — complete the prerequisite step first' : undefined}
                              style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: G, color: '#fff', fontWeight: 700, fontSize: 12, opacity: locked ? 0.45 : 1, cursor: locked ? 'default' : 'pointer' }}>
                              <i className="ti ti-upload" /> Upload
                            </button>
                          </span>
                        ) : clientStep && !locked && view.status !== 'cancelled' && (
                          <button onClick={() => pick(s.id, u.key)} disabled={busy === `up-${s.id}-${u.key}`}
                            style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, border: 'none', background: G, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: busy === `up-${s.id}-${u.key}` ? 0.6 : 1 }}>
                            {busy === `up-${s.id}-${u.key}` ? <i className="ti ti-loader-2" style={{ animation: 'spin .8s linear infinite', display: 'inline-block' }} /> : <><i className="ti ti-upload" /> Upload</>}
                          </button>
                        )}
                      </div>
                      {!uploadsBySlot(u.key).length && !clientStep && !preview && (
                        <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 6 }}><i className="ti ti-clock" /> SLF will upload this document here.</div>
                      )}
                      {!uploadsBySlot(u.key).length && clientStep && locked && !preview && (
                        <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 6 }}><i className="ti ti-lock" /> Available once the step(s) above are complete.</div>
                      )}
                      {uploadsBySlot(u.key).map((f: any) => (
                        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, marginTop: 7 }}>
                          <i className={/^image\//.test(f.mime) ? 'ti ti-photo' : 'ti ti-file'} style={{ color: G }} />
                          <a href={fileUrl(f.id)} style={{ color: '#111827', fontWeight: 600, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</a>
                          <span style={{ color: '#9CA3AF', fontSize: 10.5, whiteSpace: 'nowrap' }}>{f.byParty === 'client' ? 'you' : 'SLF'} · {Math.round((f.size || 0) / 1024)} KB</span>
                          {f.byParty === 'client' && <button onClick={() => delFile(f.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9CA3AF' }}><i className="ti ti-x" /></button>}
                        </div>
                      ))}
                    </div>
                  ))}
                  {/* Files uploaded outside declared slots (e.g. SLF deliverables) */}
                  {(s.files || []).filter((f: any) => !(s.uploads || []).some((u: any) => u.key === f.slot) && !(f.slot === 'example' && /^image\//.test(f.mime))).map((f: any) => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, marginTop: 8 }}>
                      <i className={/^image\//.test(f.mime) ? 'ti ti-photo' : 'ti ti-file'} style={{ color: G }} />
                      <a href={fileUrl(f.id)} style={{ color: '#111827', fontWeight: 600, textDecoration: 'none' }}>{f.filename}</a>
                      <span style={{ color: '#9CA3AF', fontSize: 10.5 }}>{f.byParty === 'client' ? 'you' : 'SLF'} · {Math.round((f.size || 0) / 1024)} KB</span>
                      {f.byParty === 'client' && <button onClick={() => delFile(f.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9CA3AF' }}><i className="ti ti-x" /></button>}
                    </div>
                  ))}

                  {/* Confirm button */}
                  {s.confirmLabel && (preview || (clientStep && !locked && !s.confirmed && s.status !== 'complete' && view.status !== 'cancelled')) && (
                    <button onClick={() => confirm(s.id)} disabled={busy === `cf-${s.id}` || (!!preview && (locked || s.confirmed))}
                      style={{ marginTop: 12, padding: '9px 18px', borderRadius: 9, border: 'none', background: '#1D4ED8', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: busy === `cf-${s.id}` || (preview && (locked || s.confirmed)) ? 0.5 : 1 }}>
                      {busy === `cf-${s.id}` ? <i className="ti ti-loader-2" style={{ animation: 'spin .8s linear infinite', display: 'inline-block' }} /> : <><i className="ti ti-circle-check" /> {s.confirmLabel}</>}
                    </button>
                  )}
                  {s.confirmed && s.status !== 'complete' && (
                    <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: '#1D4ED8' }}><i className="ti ti-check" /> Confirmed — awaiting SLF review</div>
                  )}

                  {/* Comments */}
                  <details style={{ marginTop: 12 }} open={(s.comments || []).length > 0}>
                    <summary style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', cursor: 'pointer' }}>Comments ({(s.comments || []).length})</summary>
                    <div style={{ marginTop: 8 }}>
                      {(s.comments || []).map((c: any, k: number) => (
                        <div key={k} style={{ borderLeft: `2px solid ${c.party === 'client' ? '#1D4ED8' : G}`, paddingLeft: 9, marginBottom: 8 }}>
                          <div style={{ fontSize: 10.5, color: '#9CA3AF' }}><b style={{ color: '#4B5563' }}>{c.party === 'client' ? c.name || 'You' : 'SLF'}</b> · {fmtTs(c.at)}</div>
                          <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{c.body}</div>
                        </div>
                      ))}
                      {(preview || view.status !== 'cancelled') && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          {/* Multi-line, like the staff side (owner 2026-08-25): clients paste
                              checklists too. Enter breaks the line; Ctrl/Cmd+Enter posts. */}
                          <textarea value={note[s.id] || ''} onChange={e => setNote(pr => ({ ...pr, [s.id]: e.target.value }))}
                            placeholder="Write a comment… (Enter = new line, Ctrl+Enter = post)"
                            rows={Math.min(6, Math.max(1, (note[s.id] || '').split('\n').length))}
                            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) { e.preventDefault(); comment(s.id); } }}
                            style={{ flex: 1, padding: '7px 10px', fontSize: 12.5, border: '1px solid #D1D5DB', borderRadius: 8, outline: 'none', resize: 'vertical', minHeight: 34, lineHeight: 1.45, fontFamily: 'inherit' }} />
                          <button onClick={() => comment(s.id)} disabled={busy === `cm-${s.id}`}
                            style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: G, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: busy === `cm-${s.id}` ? 0.6 : 1 }}>Post</button>
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginTop: 26 }}>
          This workflow is a general guide to the process and does not constitute legal advice.<br />
          {String(view.companyName || 'SLF Legal Co., Ltd.')}
        </div>
      </div>
    </div>
  );
}

const Row = ({ l, v, strong = false }: { l: string; v: string; strong?: boolean }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: strong ? 15 : 13, fontWeight: strong ? 800 : 500, padding: '3px 0' }}>
    <span style={{ color: strong ? '#111827' : '#6B7280' }}>{l}</span><span>{v}</span>
  </div>
);
